import { useQuery, useMutation } from '@tanstack/react-query'
import { centreAPIs } from 'src/utils/api'
import { ICompany, IPosition } from 'src/api/api'
import { BulkDeletePositionRequest, GetPositionsRequest } from 'src/api/positions'

export const COMPANY_POSITIONS_QUERY_KEY = 'company-positions'
export const usePositions = ({
  queryParamKey = 'company_slug',
  queryParamValue,
}: GetPositionsRequest) => {
  return useQuery({
    queryKey: [COMPANY_POSITIONS_QUERY_KEY, { queryParamKey, queryParamValue }],
    queryFn: async () => {
      let [err, response] = await centreAPIs.getPositions({ queryParamKey, queryParamValue })

      if (err) throw err
      return response.data.data
    },
    enabled: !!queryParamValue,
    gcTime: 0,
  })
}

export const usePositionMutations = () => {
  let { mutateAsync: savePosition } = useMutation({
    mutationFn: async ({
      position,
      companyId,
      locationRemoteId,
    }: {
      position: Position
      locationRemoteId: string
      companyId: number
    }) => {
      if (position.id) {
        let [err, response] = await centreAPIs.updatePosition({
          ...position,
        })

        if (err) throw err
        return [err, response]
      }

      let [err, response] = await centreAPIs.createPosition({
        ...position,
        company_id: companyId,
        remote_elevator_id: locationRemoteId,
      })

      if (err) throw err
      return [err, response]
    },
  })

  let { mutateAsync: deletePosition } = useMutation({
    mutationFn: async ({ position }: { position: Position }) => {
      let [err, response] = await centreAPIs.deletePosition({
        id: position.id,
      })

      if (err) throw err

      return [err, response]
    },
  })

  return { savePosition, deletePosition }
}

export const useBulkPositionMutations = () => {
  let { mutateAsync: saveBulkPositions } = useMutation({
    mutationFn: async ({
      company_id,
      remote_id,
      display_name,
      elevators,
      ids,
    }: {
      remote_id: IPosition['remote_id']
      display_name: IPosition['display_name']
      elevators?: number[]
      company_id?: ICompany['id']
      ids?: number[]
    }) => {
      // Bulk Create Positions
      if (!ids) {
        let [err, response] = await centreAPIs.bulkCreatePositions({
          company_id,
          remote_id,
          display_name,
          elevators,
        })
        return [err, response]
      }
      // Bulk Update Positions
      let [err, response] = await centreAPIs.bulkUpdatePositions({
        remote_id,
        display_name,
        ids,
        elevators,
      })
      return [err, response]
    },
  })

  let { mutateAsync: deleteBulkPositions } = useMutation({
    mutationFn: async ({ company_id, remote_id, ids }: Partial<BulkDeletePositionRequest>) => {
      if (ids) {
        let [err, response] = await centreAPIs.bulkDeletePositions({
          company_id,
          remote_id,
          ids,
        })
        return [err, response]
      }
    },
  })

  return { saveBulkPositions, deleteBulkPositions }
}
